/**
 * /audio-cutter (SEO_BRIEF_11 section 2.1): an audio file or a video in; its waveform with a start
 * and an end handle; −0.1 s / +0.1 s on each; the selection played; a fade in and a fade out; the
 * cut saved as MP3 (128, 192 or 320 kbit/s) or WAV.
 *
 * The page (tools/audio_cut.html) loads this bundle beside boot.js, the shared shell. It owns the
 * drop zone, the waveform and its handles, the player and Save, and starts the worker
 * (`audio-cut-worker.ts`, where mediabunny and the encoder live) on the first file, so a visitor
 * who never drops one never downloads it.
 *
 * PHONES FIRST. The handles are pointer events, so a finger, a pen and a mouse drag them the same
 * way: each handle takes the top or the bottom half of the strip at its place (CSS), so two
 * handles side by side can both be grabbed, and stops the page scrolling under the finger
 * (`touch-action: none`); the rest of the strip lets the page scroll up and down, and a tap or a
 * sideways drag there moves the nearer handle. Each handle is a slider for the keyboard and for
 * a screen reader too (lib/cut-wave.ts `keyMove`).
 *
 * PLAYING THE SELECTION. The file itself in an <audio> element, from the start handle to the end
 * one, with the fades as its volume where the browser lets a page set it (iPhones keep it at full
 * volume). A file the element cannot play (an MKV in Safari) is cut by the worker as WAV, at most
 * its first five minutes (PREVIEW_MAX_S, so a phone holds it), and that is played instead, fades
 * and all; a phone that wants a second tap for it gets the button back. While a cut is being
 * made, Play waits: that WAV would be a second cut, and starting it would stop the first.
 *
 * A STALE CUT IS NEVER SAVED. Download is offered only for the selection, fades and output on
 * screen: moving a handle or changing an option takes it away, and stops a cut still running.
 *
 * Every word is in the template (`data-cut-label`, the error paragraphs); this file writes only
 * times, sizes, file names and format names, which are the same in every language.
 *
 * THE USAGE COUNTER (base/src/lib/usage.ts), under the root's `data-usage-tool` slug: "loaded" once
 * per file whose waveform was read, "failed" once per file that could not be read, "downloaded"
 * for each Save, as mp3 or wav; a file loaded and never saved is the counter's own "no_result".
 */

import { mountDropzone } from '../../base/src/lib/dropzone.ts';
import { fileSize } from '../../base/src/lib/format.ts';
import { usageCounter } from '../../base/src/lib/usage.ts';
import {
  clockTenths,
  fadeLengths,
  gainAt,
  type Handle,
  moveHandle,
  nudgeHandle,
  outputKbps,
  parseOutput,
  previewSelection,
  type Selection,
  selectionLength,
  wholeSelection,
} from '../../../../src/media/audio-cut-math.ts';
import { mp3WasmUrl } from '../../../../src/media/mp3-assets.ts';
import type { Analysis, CutFailure, CutFromWorker, CutToWorker } from './lib/cut-protocol.ts';
import { keyMove, nearestHandle, normalise, poolPeaks, timeAtX, xAtTime } from './lib/cut-wave.ts';
import { droppedFormat, routeDrop, savedFormat } from './lib/labels.ts';

/** The banner each refusal unhides: the engine's reasons, and a file that is not sound or video. */
type ErrorKey = CutFailure | 'wrong-file';
type JobKind = 'analyse' | 'cut' | 'preview';

/** A tap that moves this far sideways on the strip is a drag. */
const DRAG_PX = 6;
/** The waveform's bars: width and gap, in CSS pixels. */
const BAR_PX = 3;
const GAP_PX = 1;

function buildId(): string | null {
  return document.querySelector<HTMLMetaElement>('meta[name="build-id"]')?.content.trim() || null;
}

/** Same build-id rule as the other workers: never yesterday's bundle. */
function workerUrl(): string {
  const build = buildId();
  return build
    ? `/static/build/audio/audio-cut-worker.js?v=${encodeURIComponent(build)}`
    : '/static/build/audio/audio-cut-worker.js';
}

function q<T extends Element = HTMLElement>(root: ParentNode, sel: string): T | null {
  return root.querySelector<T>(sel);
}

function saveBlob(blob: Blob, name: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  a.rel = 'noopener';
  a.style.display = 'none';
  document.body.append(a);
  a.click();
  a.remove();
  // Revoking in the same task cancels the download in some engines.
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

const pct = (t: number, duration: number): string =>
  `${duration > 0 ? Math.min(100, Math.max(0, (t / duration) * 100)) : 0}%`;

function mount(root: HTMLElement): void {
  const host = q(root, '[data-dropzone-host]');
  const working = q(root, '[data-cut-working]');
  const bar = q(root, '[data-cut-bar]');
  const fill = q(root, '[data-cut-fill]');
  const cancel = q<HTMLButtonElement>(root, '[data-cut-cancel]');
  const editor = q(root, '[data-cut-editor]');
  const wave = q(root, '[data-cut-wave]');
  const canvas = q<HTMLCanvasElement>(root, '[data-cut-canvas]');
  const playhead = q(root, '[data-cut-playhead]');
  const playBtn = q<HTMLButtonElement>(root, '[data-cut-play]');
  const outputSel = q<HTMLSelectElement>(root, '[data-cut-output]');
  const runBtn = q<HTMLButtonElement>(root, '[data-cut-run]');
  const result = q(root, '[data-cut-result]');
  const save = q<HTMLButtonElement>(root, '[data-cut-save]');
  const status = q(root, '[data-cut-status]');
  if (!host || !editor || !wave || !save || !runBtn) return;
  const handles: Record<Handle, HTMLElement | null> = {
    start: q(wave, '[data-cut-handle="start"]'),
    end: q(wave, '[data-cut-handle="end"]'),
  };
  const shades = {
    before: q(wave, '[data-cut-shade="before"]'),
    after: q(wave, '[data-cut-shade="after"]'),
  };
  const fadeBoxes = {
    in: q<HTMLInputElement>(root, '[data-cut-fade="in"]'),
    out: q<HTMLInputElement>(root, '[data-cut-fade="out"]'),
  };

  const usageTool = root.dataset['usageTool'] ?? '';
  const usage = usageTool ? usageCounter(usageTool) : null;

  const label = (key: string): string =>
    q(root, `[data-cut-label="${key}"]`)?.textContent?.trim() ?? '';
  const slot = (scope: ParentNode, name: string, text: string) => {
    const el = q(scope, `[data-slot="${name}"]`);
    if (el) el.textContent = text;
  };

  /* ── state ───────────────────────────────────────────────────────────── */

  let worker: Worker | null = null;
  let nextId = 1;
  let job: { id: number; kind: JobKind; file: File; key: string } | null = null;
  let file: File | null = null;
  let analysis: Analysis | null = null;
  let sel: Selection = { start: 0, end: 0 };
  let done: { blob: Blob; name: string; container: 'mp3' | 'wav'; key: string } | null = null;

  const duration = () => analysis?.durationS ?? 0;
  const fades = () => fadeLengths(sel, !!fadeBoxes.in?.checked, !!fadeBoxes.out?.checked);
  const previewKey = () =>
    `${sel.start}|${sel.end}|${!!fadeBoxes.in?.checked}|${!!fadeBoxes.out?.checked}`;
  const cutKey = () => `${previewKey()}|${parseOutput(outputSel?.value)}`;

  /* ── what the page shows ─────────────────────────────────────────────── */

  const showError = (key: ErrorKey | null) => {
    for (const el of root.querySelectorAll<HTMLElement>('[data-cut-error]')) {
      el.hidden = el.dataset['cutError'] !== key;
    }
  };
  const setStatus = (text: string) => {
    if (status) status.textContent = text;
  };
  const setProgress = (fraction: number | null) => {
    if (bar) bar.dataset['indeterminate'] = fraction === null ? 'true' : 'false';
    const value = fraction === null ? 0 : Math.round(fraction * 100);
    fill?.style.setProperty('--progress-pct', `${value}%`);
    for (const el of root.querySelectorAll<HTMLElement>('[data-cut-working] [data-slot="pct"]')) {
      el.textContent = String(value);
    }
  };
  const setWorking = (kind: JobKind | null) => {
    // A preview is quick and shows on the Play button, not as a progress bar.
    const shown = kind === 'analyse' || kind === 'cut';
    if (working) working.hidden = !shown;
    for (const el of root.querySelectorAll<HTMLElement>('[data-cut-stage]')) {
      el.hidden = el.dataset['cutStage'] !== kind;
    }
    if (shown) setProgress(null);
    runBtn.disabled = kind === 'cut';
    // Play waits for a cut: a file the player can't play is heard through a cut of its own, and
    // starting that one would stop this one. A preview keeps Play busy itself (setPlayButton).
    if (playBtn && kind !== 'preview') playBtn.disabled = kind === 'cut';
  };
  const clearResult = () => {
    done = null;
    save.disabled = true;
    if (result) result.hidden = true;
  };
  /** The Download button names the type "Save as" is on: MP3 or WAV. */
  const showFormat = () => {
    slot(save, 'format', outputKbps(parseOutput(outputSel?.value)) === null ? 'WAV' : 'MP3');
  };
  showFormat();

  /** The handles, the shading, the times and the sliders' values, from `sel`. */
  const render = () => {
    const d = duration();
    for (const which of ['start', 'end'] as const) {
      const value = sel[which];
      const handle = handles[which];
      if (handle) {
        handle.style.left = pct(value, d);
        handle.setAttribute('aria-valuemax', String(Math.round(d * 10) / 10));
        handle.setAttribute('aria-valuenow', String(Math.round(value * 10) / 10));
        handle.setAttribute('aria-valuetext', clockTenths(value));
      }
      const out = q(root, `[data-cut-value="${which}"]`);
      if (out) out.textContent = clockTenths(value);
    }
    if (shades.before) shades.before.style.width = pct(sel.start, d);
    if (shades.after) shades.after.style.left = pct(sel.end, d);
    slot(
      root.querySelector('[data-cut-length]') ?? root,
      'length',
      clockTenths(selectionLength(sel)),
    );
  };

  /** The waveform, at the strip's size and the screen's density. */
  const draw = () => {
    if (!canvas || !analysis) return;
    const width = wave.clientWidth;
    const height = wave.clientHeight;
    if (width <= 0 || height <= 0) return;
    const dpr = Math.min(3, window.devicePixelRatio || 1);
    canvas.width = Math.round(width * dpr);
    canvas.height = Math.round(height * dpr);
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, width, height);
    const columns = Math.max(1, Math.floor(width / (BAR_PX + GAP_PX)));
    const step = width / columns;
    const bars = normalise(
      poolPeaks(analysis.peaks, analysis.peakRate, analysis.durationS, columns),
    );
    ctx.fillStyle = getComputedStyle(canvas).color;
    const mid = height / 2;
    const room = height - 8;
    for (let i = 0; i < columns; i++) {
      const h = Math.max(1, (bars[i] ?? 0) * room);
      ctx.fillRect(i * step, mid - h / 2, BAR_PX, h);
    }
  };
  if (typeof ResizeObserver === 'function') new ResizeObserver(draw).observe(wave);
  else window.addEventListener('resize', draw);

  /** A new selection: what Save would hand back, or a cut still running, no longer matches it. */
  const setSelection = (next: Selection) => {
    if (next.start === sel.start && next.end === sel.end) return;
    sel = next;
    if (playing?.mode === 'preview') stopPlayback();
    staleCut();
    render();
  };

  /* ── playing the selection ───────────────────────────────────────────── */

  const audio = document.createElement('audio');
  audio.preload = 'metadata';
  audio.setAttribute('playsinline', '');
  let sourceUrl: string | null = null;
  /** The element could not play this file, so the selection is played as a WAV cut. */
  let nativeFailed = false;
  let preview: { url: string; key: string } | null = null;
  let playing: { mode: 'native' | 'preview'; offset: number; raf: number } | null = null;

  const setVolume = (v: number) => {
    try {
      audio.volume = v;
    } catch {
      /* some browsers fix the volume; the cut itself carries the fades */
    }
  };
  const setPlayButton = (on: boolean, busy = false) => {
    if (!playBtn) return;
    playBtn.textContent = label(on ? 'stop' : 'play');
    playBtn.setAttribute('aria-pressed', on ? 'true' : 'false');
    playBtn.disabled = busy || job?.kind === 'cut';
  };
  const stopPlayback = () => {
    if (!playing) return;
    cancelAnimationFrame(playing.raf);
    playing = null;
    audio.pause();
    setVolume(1);
    if (playhead) playhead.hidden = true;
    setPlayButton(false);
  };
  const tick = () => {
    if (!playing) return;
    const t = playing.offset + audio.currentTime;
    if (audio.ended || t >= sel.end) {
      stopPlayback();
      return;
    }
    if (playing.mode === 'native') setVolume(gainAt(t, sel, fades()));
    if (playhead) {
      playhead.hidden = false;
      playhead.style.left = pct(t, duration());
    }
    playing.raf = requestAnimationFrame(tick);
  };
  const begin = (mode: 'native' | 'preview', offset: number) => {
    playing = { mode, offset, raf: 0 };
    setPlayButton(true);
    audio.play().then(
      () => {
        if (playing) playing.raf = requestAnimationFrame(tick);
      },
      (err: unknown) => {
        const name = err instanceof DOMException ? err.name : '';
        stopPlayback();
        // NotAllowedError: the phone wants the tap itself; the button is back for it.
        if (mode === 'native' && name !== 'NotAllowedError' && name !== 'AbortError') {
          nativeFailed = true;
          playPreview();
        }
      },
    );
  };
  const playNative = () => {
    if (!file) return;
    if (sourceUrl === null) sourceUrl = URL.createObjectURL(file);
    if (audio.src !== sourceUrl) audio.src = sourceUrl;
    const at = sel.start;
    try {
      audio.currentTime = at;
    } catch {
      /* before its metadata some engines refuse a seek; it is set again once they have it */
    }
    if (audio.readyState === 0) {
      audio.addEventListener(
        'loadedmetadata',
        () => {
          if (playing?.mode === 'native') audio.currentTime = sel.start;
        },
        { once: true },
      );
    }
    setVolume(gainAt(at, sel, fades()));
    begin('native', 0);
  };
  const playPreview = () => {
    // A cut running is never stopped for a preview: Play is off until it ends (setWorking).
    if (!file || job?.kind === 'cut') return;
    const key = previewKey();
    // The player's error and its refused play() both land here; one WAV is enough.
    if (job?.kind === 'preview' && job.key === key) return;
    if (preview && preview.key === key) {
      audio.src = preview.url;
      begin('preview', sel.start);
      return;
    }
    setPlayButton(false, true);
    startJob('preview', file, key);
  };
  audio.addEventListener('error', () => {
    if (playing?.mode !== 'native') return;
    stopPlayback();
    nativeFailed = true;
    playPreview();
  });
  playBtn?.addEventListener('click', () => {
    if (!analysis) return;
    if (playing) {
      stopPlayback();
      return;
    }
    if (nativeFailed) playPreview();
    else playNative();
  });

  /* ── the worker ──────────────────────────────────────────────────────── */

  const onAnalysed = (f: File, answer: Extract<CutFromWorker, { kind: 'analysed' }>['result']) => {
    setWorking(null);
    if (!answer.ok) {
      if (answer.reason === 'cancelled') {
        setStatus(label('cancelled'));
        return;
      }
      console.warn('[audio-cut]', answer.reason, answer.detail);
      usage?.failed(f, droppedFormat(f));
      showError(answer.reason);
      setStatus('');
      return;
    }
    usage?.loaded(f, droppedFormat(f));
    analysis = answer;
    sel = wholeSelection(answer.durationS);
    slot(editor, 'file', f.name);
    slot(editor, 'duration', clockTenths(answer.durationS));
    editor.hidden = false;
    setStatus('');
    render();
    draw();
  };

  const onCut = (
    kind: JobKind,
    key: string,
    answer: Extract<CutFromWorker, { kind: 'cut' }>['result'],
  ) => {
    setWorking(null);
    if (kind === 'preview') {
      setPlayButton(false);
      if (!answer.ok) {
        if (answer.reason !== 'cancelled') {
          console.warn('[audio-cut]', answer.reason, answer.detail);
          showError(answer.reason);
        }
        return;
      }
      if (preview) URL.revokeObjectURL(preview.url);
      preview = { url: URL.createObjectURL(answer.blob), key };
      // Still the selection that was asked for: play it. A phone may want a fresh tap.
      if (key === previewKey()) {
        audio.src = preview.url;
        begin('preview', sel.start);
      }
      return;
    }
    if (!answer.ok) {
      if (answer.reason === 'cancelled') {
        setStatus(label('cancelled'));
        return;
      }
      console.warn('[audio-cut]', answer.reason, answer.detail);
      showError(answer.reason);
      setStatus('');
      return;
    }
    const { blob } = answer;
    done = { blob, name: answer.name, container: answer.container, key };
    const kbps = outputKbps(parseOutput(key.split('|').pop()));
    const type = kbps ? `MP3 · ${kbps} kbps` : 'WAV';
    if (result) {
      slot(
        result,
        'type',
        `${type} · ${answer.name} · ${fileSize(blob.size)} · ${clockTenths(answer.durationS)}`,
      );
      result.hidden = false;
    }
    save.disabled = false;
    setStatus(label('ready'));
  };

  const onMessage = (event: MessageEvent<CutFromWorker>) => {
    const reply = event.data;
    if (job === null || reply.id !== job.id) return;
    if (reply.kind === 'progress') {
      setProgress(reply.fraction);
      return;
    }
    const { kind, file: f, key } = job;
    job = null;
    if (reply.kind === 'analysed') onAnalysed(f, reply.result);
    else onCut(kind, key, reply.result);
  };

  const start = (): Worker => {
    if (worker) return worker;
    const w = new Worker(workerUrl(), { type: 'module' });
    w.onmessage = onMessage;
    w.onerror = (event) => {
      // A worker that failed to load (offline, blocked) answers the waiting file with the same
      // banner as an unreadable one rather than leaving the page on a progress bar.
      event.preventDefault();
      w.terminate();
      worker = null;
      if (job) {
        const { file: f, kind } = job;
        job = null;
        setWorking(null);
        setPlayButton(false);
        if (kind === 'analyse') usage?.failed(f, droppedFormat(f));
        showError('unreadable');
      }
    };
    worker = w;
    return w;
  };

  const stopJob = () => {
    if (job === null) return;
    worker?.postMessage({ kind: 'cancel', id: job.id } satisfies CutToWorker);
    if (job.kind === 'preview') setPlayButton(false);
    job = null;
    setWorking(null);
  };

  /** A cut running, or made, for a selection or options since changed: neither is kept. */
  const staleCut = () => {
    if (job?.kind === 'cut' && job.key !== cutKey()) {
      stopJob();
      setStatus('');
    }
    if (done && done.key !== cutKey()) clearResult();
  };

  function startJob(kind: JobKind, f: File, key: string): void {
    stopJob();
    showError(null);
    const id = nextId++;
    job = { id, kind, file: f, key };
    setWorking(kind);
    if (kind === 'analyse') {
      start().postMessage({ kind: 'analyse', id, file: f } satisfies CutToWorker);
      return;
    }
    const output = kind === 'preview' ? 'wav' : parseOutput(outputSel?.value);
    const span = kind === 'preview' ? previewSelection(sel) : sel;
    start().postMessage({
      kind: 'cut',
      id,
      file: f,
      inputName: f.name,
      start: span.start,
      end: span.end,
      fadeIn: !!fadeBoxes.in?.checked,
      // A preview of only the first five minutes stops short of the selection's end, where the
      // fade out is: fading there would put silence mid-selection that the cut doesn't have.
      fadeOut: !!fadeBoxes.out?.checked && span.end === sel.end,
      output,
      wasmUrl: outputKbps(output) === null ? null : mp3WasmUrl(buildId()),
    } satisfies CutToWorker);
  }

  /* ── input ───────────────────────────────────────────────────────────── */

  const reset = () => {
    stopPlayback();
    stopJob();
    clearResult();
    analysis = null;
    editor.hidden = true;
    nativeFailed = false;
    audio.removeAttribute('src');
    if (sourceUrl) URL.revokeObjectURL(sourceUrl);
    sourceUrl = null;
    if (preview) URL.revokeObjectURL(preview.url);
    preview = null;
  };

  mountDropzone(host, {
    onFiles: (dropped) => {
      // One file at a time: the first one dropped is the one cut.
      const f = dropped[0];
      if (!f) return;
      reset();
      const route = routeDrop(f, true);
      if (route === 'start') {
        file = f;
        setStatus(label('reading'));
        startJob('analyse', f, '');
        return;
      }
      file = null;
      setStatus('');
      usage?.failed(f, droppedFormat(f));
      // A WMV, FLV, MPG… gets the sentence that lists the files that work; anything that is
      // neither sound nor video is the wrong file.
      showError(route === 'video-format' ? 'unreadable' : 'wrong-file');
    },
  });

  cancel?.addEventListener('click', () => {
    stopJob();
    setStatus(label('cancelled'));
  });

  /* ── the handles ─────────────────────────────────────────────────────── */

  const timeAt = (clientX: number) => {
    const rect = wave.getBoundingClientRect();
    return timeAtX(clientX - rect.left, rect.width, duration());
  };
  /** Where a handle's line is on screen: the middle of its 44px. */
  const handleX = (which: Handle) => {
    const rect = wave.getBoundingClientRect();
    return rect.left + xAtTime(sel[which], rect.width, duration());
  };
  const move = (which: Handle, to: number) => setSelection(moveHandle(sel, which, to, duration()));

  /**
   * A finger on a handle (dragging), or on the strip (a tap, or a drag once it goes sideways).
   * `grab` is how far from the handle's line the finger landed: the handle keeps that distance,
   * so it never jumps under the finger on the first move. A drag from the strip brings the
   * nearer handle to the finger, as a tap does, so its grab is 0.
   */
  let drag: { which: Handle; pointerId: number; grab: number } | null = null;
  let pending: { pointerId: number; x: number } | null = null;

  wave.addEventListener('pointerdown', (ev) => {
    if (!analysis || (ev.pointerType === 'mouse' && ev.button !== 0)) return;
    const handle = (ev.target as Element | null)?.closest<HTMLElement>('[data-cut-handle]');
    const which = handle?.dataset['cutHandle'];
    if (which === 'start' || which === 'end') {
      drag = { which, pointerId: ev.pointerId, grab: ev.clientX - handleX(which) };
      wave.setPointerCapture?.(ev.pointerId);
      stopPlayback();
      handle?.focus({ preventScroll: true });
      ev.preventDefault();
      return;
    }
    pending = { pointerId: ev.pointerId, x: ev.clientX };
  });
  wave.addEventListener('pointermove', (ev) => {
    if (
      pending &&
      ev.pointerId === pending.pointerId &&
      Math.abs(ev.clientX - pending.x) > DRAG_PX
    ) {
      const which = nearestHandle(timeAt(pending.x), sel);
      pending = null;
      drag = { which, pointerId: ev.pointerId, grab: 0 };
      wave.setPointerCapture?.(ev.pointerId);
      stopPlayback();
    }
    if (drag && ev.pointerId === drag.pointerId) {
      move(drag.which, timeAt(ev.clientX - drag.grab));
    }
  });
  wave.addEventListener('pointerup', (ev) => {
    if (pending && ev.pointerId === pending.pointerId) {
      // A tap on the strip: the nearer handle comes to it.
      const t = timeAt(ev.clientX);
      move(nearestHandle(t, sel), t);
    }
    pending = null;
    if (drag && ev.pointerId === drag.pointerId) drag = null;
  });
  wave.addEventListener('pointercancel', () => {
    // The page took the gesture (a scroll): nothing moves.
    pending = null;
    drag = null;
  });

  for (const which of ['start', 'end'] as const) {
    handles[which]?.addEventListener('keydown', (ev) => {
      const action = keyMove(ev.key);
      if (action === null) return;
      ev.preventDefault();
      if ('by' in action) setSelection(nudgeHandle(sel, which, action.by, duration()));
      else move(which, action.to === 'min' ? 0 : duration());
    });
  }
  for (const btn of root.querySelectorAll<HTMLButtonElement>('[data-cut-nudge]')) {
    btn.addEventListener('click', () => {
      const which = btn.dataset['cutNudge'];
      const by = Number(btn.dataset['cutBy']);
      if ((which === 'start' || which === 'end') && Number.isFinite(by)) {
        setSelection(nudgeHandle(sel, which, by, duration()));
      }
    });
  }

  /* ── options, cut, save ──────────────────────────────────────────────── */

  const optionsChanged = () => {
    if (playing?.mode === 'preview') stopPlayback();
    showFormat();
    staleCut();
  };
  fadeBoxes.in?.addEventListener('change', optionsChanged);
  fadeBoxes.out?.addEventListener('change', optionsChanged);
  outputSel?.addEventListener('change', optionsChanged);

  runBtn.addEventListener('click', () => {
    if (!file || !analysis) return;
    stopPlayback();
    clearResult();
    setStatus(label('cutting'));
    startJob('cut', file, cutKey());
  });

  save.addEventListener('click', () => {
    if (!done) return;
    saveBlob(done.blob, done.name);
    usage?.downloaded(savedFormat(done.container));
    setStatus(label('saved'));
  });
}

const root = document.querySelector<HTMLElement>('[data-audio-cut]');
if (root) mount(root);
